import {
  CSSProperties,
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FLOATING_VIDEO_Z_INDEX } from '../../libs/overlayZIndex';

export const PRIMIS_OUTSTREAM_CONTAINER_ID = 'primis-player-container';
export const PRIMIS_PLAYER_SCRIPT_SRC = 'https://live.primis.tech/live/liveView.php?s=122375';
export const PRIMIS_FLOATING_FALLBACK_SCRIPT_SRC = `${PRIMIS_PLAYER_SCRIPT_SRC}&videoType=sticky`;
export const PRIMIS_DOCKED_PLAYER_SCRIPT_SRC = 'https://live.primis.tech/live/liveView.php?s=122580&videoType=flow&flowMode=both';
export const PRIMIS_VENDOR_PLAYER_CONTAINER_ID = 'primisPlayerContainerDiv';
export const PRIMIS_MOBILE_MEDIA_QUERY = '(max-width: 767px)';

const primisOutstreamContainerStyle: CSSProperties = {
  height: 0,
  minHeight: 0,
  lineHeight: 0,
  fontSize: 0,
  overflow: 'visible'
};

// Primis inserts this body-level wrapper around its sticky player. Constrain the
// wrapper's stacking context so vendor-owned descendants cannot cover site chrome.
// Resize the vendor's fixed mobile player directly: transforming its ancestor makes
// position: fixed resolve against the document and strands the player below the viewport.
const primisFloatingLayerStyle = `
body > div:has(> #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID}) {
  position: relative;
  z-index: ${FLOATING_VIDEO_Z_INDEX} !important;
}

@media (max-width: 767px) {
  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} [id^="primis_playerSekindoSPlayer"] {
    right: 5px !important;
    bottom: 105px !important;
    left: auto !important;
    top: auto !important;
  }

  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} [id^="primis_playerSekindoSPlayer"],
  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} #flowCover,
  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} [id^="Player-Div-SekindoSPlayer"],
  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} [id^="Video-Div-SekindoSPlayer"],
  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} [id^="Video-iFrame-SekindoSPlayer"] {
    width: 180px !important;
    height: 101.25px !important;
  }
}
`;

const usePrimisRegistrationEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
const primisPlayerArtifactSelector = [
  `#${PRIMIS_VENDOR_PLAYER_CONTAINER_ID}`,
  '[id^="primis_container_div_"]',
  '[id^="primis_playerSekindoSPlayer"]',
  '[id^="Player-Div-SekindoSPlayer"]',
  '[id^="Video-Div-SekindoSPlayer"]',
  '[id^="Video-iFrame-SekindoSPlayer"]',
  '#contextMenuPrimis',
  '#contextMenusPrimisLogo',
  '#primisLogoWrapper',
  '#primisLogo',
  '#primisLogoTxt',
  '#primisVastIconsContainer',
  'script[src*="live.primis.tech/live/liveView.php"]'
].join(',');

type TPrimisPlacementContext = {
  dockedPlacementCount: number;
  placementMode: 'desktop' | 'mobile' | null;
  registerDockedPlacement: () => { unregister: () => void };
};

const PrimisPlacementContext = createContext<TPrimisPlacementContext>({
  dockedPlacementCount: 0,
  placementMode: null,
  registerDockedPlacement: () => ({
    unregister: () => {
      /* nop */
    }
  })
});

function createPrimisScript(src: string) {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = src;
  script.setAttribute('language', 'javascript');
  return script;
}

function removePrimisPlayerArtifactsOutsideDockedSlots() {
  document.querySelectorAll(primisPlayerArtifactSelector).forEach((element) => {
    if (element.closest('[data-primis-docked-slot="true"]')) {
      return;
    }

    element.remove();
  });
}

type TPrimisPlayerTagProps = {
  id?: string;
  scriptSrc: string;
  style?: CSSProperties;
};

const PrimisPlayerTag: FC<TPrimisPlayerTagProps> = ({ id, scriptSrc, style }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const script = createPrimisScript(scriptSrc);
    container.appendChild(script);

    return () => {
      script.remove();
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      removePrimisPlayerArtifactsOutsideDockedSlots();
    };
  }, [scriptSrc]);

  return <div id={id} ref={containerRef} style={style} />;
};

export const PrimisPlacementProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [dockedPlacementCount, setDockedPlacementCount] = useState(0);
  const [placementMode, setPlacementMode] = useState<'desktop' | 'mobile' | null>(null);

  usePrimisRegistrationEffect(() => {
    setPlacementMode(window.matchMedia(PRIMIS_MOBILE_MEDIA_QUERY).matches ? 'mobile' : 'desktop');
  }, []);

  const registerDockedPlacement = useCallback(() => {
    let isRegistered = true;
    setDockedPlacementCount((count) => count + 1);

    return {
      unregister: () => {
        if (!isRegistered) {
          return;
        }

        isRegistered = false;
        setDockedPlacementCount((count) => count - 1);
      }
    };
  }, []);
  const value = useMemo(
    () => ({
      dockedPlacementCount,
      placementMode,
      registerDockedPlacement
    }),
    [dockedPlacementCount, placementMode, registerDockedPlacement]
  );

  return <PrimisPlacementContext.Provider value={value}>{children}</PrimisPlacementContext.Provider>;
};

export function usePrimisPlacementMode() {
  return useContext(PrimisPlacementContext).placementMode;
}

export function usePrimisDockedPlacementRegistration(enabled: boolean) {
  const { registerDockedPlacement } = useContext(PrimisPlacementContext);

  usePrimisRegistrationEffect(() => {
    if (!enabled) {
      return;
    }

    const registration = registerDockedPlacement();

    return () => {
      registration.unregister();
    };
  }, [enabled, registerDockedPlacement]);
}

export function buildPrimisDockedPlayerScriptSrc(width: number, height: number) {
  return `${PRIMIS_DOCKED_PLAYER_SCRIPT_SRC}&x=${width}&y=${height}`;
}

export const PrimisDockedPlayer: FC<{ width: number; height: number }> = ({ width, height }) => (
  <PrimisPlayerTag scriptSrc={buildPrimisDockedPlayerScriptSrc(width, height)} />
);

export const PrimisFloatingFallback: FC<{ suppressed?: boolean }> = ({ suppressed = false }) => {
  const { dockedPlacementCount, placementMode } = useContext(PrimisPlacementContext);
  const [canMountFallback, setCanMountFallback] = useState(false);

  useEffect(() => {
    const mountTimeout = window.setTimeout(() => {
      setCanMountFallback(true);
    }, 0);

    return () => {
      window.clearTimeout(mountTimeout);
    };
  }, []);

  if (!placementMode || suppressed || !canMountFallback || dockedPlacementCount > 0) {
    return null;
  }

  return (
    <>
      <style data-rio-floating-video-layer="primis">{primisFloatingLayerStyle}</style>
      <PrimisPlayerTag
        id={PRIMIS_OUTSTREAM_CONTAINER_ID}
        scriptSrc={PRIMIS_FLOATING_FALLBACK_SCRIPT_SRC}
        style={primisOutstreamContainerStyle}
      />
    </>
  );
};

export const PrimisOutstream: FC = () => (
  <PrimisPlayerTag
    id={PRIMIS_OUTSTREAM_CONTAINER_ID}
    scriptSrc={PRIMIS_PLAYER_SCRIPT_SRC}
    style={primisOutstreamContainerStyle}
  />
);
